import * as React from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'

import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useExchangeRateHandler } from '@/modules/drivers/tabs/businessTrips/hooks/useExchangeRateHandler'
import DatePickerPopup from '@/ui/date/DatePickerPopup'
import { faCalendar, faCaretDownSolid, FaIcon } from '@/lib/fa'
import { PaymentMethod } from '@/app/constants/delegation/paymentMetod'
import { getPaymentMethodName } from '@/app/common/converters/newDelegation/paymentMethodToTextConverter'


type DateEditorProps = {
  value: any
  onCommit: (newDate: string | null) => void
  placeholder?: string
}

const toISODate = (date: any): string | null => {
  if (!date) return null
  if (moment.isMoment(date)) return date.format('YYYY-MM-DD')
  if (typeof date === 'string') return moment(date).format('YYYY-MM-DD')
  return null
}

const formatDateWithDashes = (digits: string): string => {
  let result = ''
  for (let i = 0; i < digits.length; i++) {
    if (i === 4 || i === 6) result += '-'
    result += digits[i]
  }
  return result
}

const parseDateInput = (input: string): string | null => {
  const trimmed = input.trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const year = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const day = parseInt(match[3], 10)
  if (year < 1900 || year > 2100) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  const date = new Date(year, month - 1, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  )
    return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const DateEditor: React.FC<DateEditorProps> = ({
  value,
  onCommit,
  placeholder = 'RRRR-MM-DD',
}) => {
  const [inputValue, setInputValue] = React.useState('')
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const [popupPosition, setPopupPosition] = React.useState({ top: 0, left: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const cursorPosRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    setInputValue(toISODate(value) || '')
  }, [value])

  React.useEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      const pos = cursorPosRef.current
      inputRef.current.setSelectionRange(pos, pos)
      cursorPosRef.current = null
    }
  })

  React.useEffect(() => {
    if (!isPickerOpen) return undefined
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        popupRef.current &&
        !popupRef.current.contains(e.target as Node)
      ) {
        setIsPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isPickerOpen])

  const openPopup = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setPopupPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    })
    setIsPickerOpen(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart ?? newValue.length
    const newDigits = newValue.replace(/\D/g, '').slice(0, 8)
    const formatted = formatDateWithDashes(newDigits)

    const digitsBeforeCursor = newValue
      .slice(0, cursorPos)
      .replace(/\D/g, '').length
    let newCursorPos = 0
    let digitCount = 0
    for (let i = 0; i < formatted.length; i++) {
      if (digitCount === digitsBeforeCursor) break
      newCursorPos = i + 1
      if (formatted[i] !== '-') digitCount++
    }
    if (
      newCursorPos < formatted.length &&
      formatted[newCursorPos] === '-' &&
      newValue.length >= inputValue.length
    ) {
      newCursorPos++
    }

    cursorPosRef.current = newCursorPos
    setInputValue(formatted)
  }

  const handleBlur = () => {
    const parsed = parseDateInput(inputValue)
    if (parsed) {
      onCommit(parsed)
    } else if (inputValue === '') {
      onCommit(null)
    } else {
      setInputValue(toISODate(value) || '')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  const handlePopupSubmit = (fullDate: string) => {
    setInputValue(fullDate)
    onCommit(fullDate)
    setIsPickerOpen(false)
  }

  return (
    <>
      <div
        ref={containerRef}
        className="tw-flex tw-w-fit tw-items-center tw-gap-1 tw-rounded tw-py-1"
      >
        <FaIcon
          icon={faCalendar}
          className="tw-cursor-pointer"
          onClick={() => {
            if (isPickerOpen) {
              setIsPickerOpen(false)
            } else {
              openPopup()
            }
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="tw-w-[100px] tw-text-nowrap tw-bg-transparent tw-text-sm tw-text-content-base tw-placeholder-content-base/50 focus:tw-outline-none"
        />
      </div>
      {isPickerOpen &&
        createPortal(
          <div
            ref={popupRef}
            className="tw-absolute tw-z-[9999] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-lg"
            style={{ top: popupPosition.top, left: popupPosition.left }}
          >
            <DatePickerPopup
              value={toISODate(value)}
              submit={handlePopupSubmit}
            />
          </div>,
          document.body,
        )}
    </>
  )
}

const PAYMENT_METHOD_VALUES: PaymentMethod[] = [
  PaymentMethod.Cash,
  PaymentMethod.CreditCard,
  PaymentMethod.Transfer,
  PaymentMethod.Payroll,
  PaymentMethod.Other,
]

type InvoiceRowItemProps = {
  companyId: string
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  isChecked: boolean
  toggleId: (id: number) => void
  originalIndex: number
  invoice: any
  currencies: any[]
}

const InvoiceRowItem: React.FC<InvoiceRowItemProps> = ({
  companyId,
  onUpdateArrayItem,
  isChecked,
  toggleId,
  originalIndex,
  invoice,
  currencies,
}) => {
  const { t } = useLocalize()

  const {
    handleCurrencyChange,
    handleExchangeRateDateChange,
    handleExchangeRateManualChange,
    handleAmountChange,
  } = useExchangeRateHandler({
    companyId,
    onUpdateArrayItem: onUpdateArrayItem!,
    arrayName: 'invoices',
  })

  const formatAmount = (value: number): string => {
    if (value == null || isNaN(Number(value))) return '-'
    return Number(value).toFixed(2)
  }

  const formatRate = (value: number): string => {
    if (value == null || isNaN(Number(value))) return '-'
    return Number(value).toFixed(4)
  }

  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] =
    React.useState(false)
  const currencyDropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isCurrencyDropdownOpen) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCurrencyDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isCurrencyDropdownOpen])

  const handleSelectCurrency = (currency: any) => {
    if (originalIndex !== -1 && currency) {
      handleCurrencyChange(invoice, originalIndex, currency.code, currency.id)
    }
    setIsCurrencyDropdownOpen(false)
  }

  const [isPaymentMethodDropdownOpen, setIsPaymentMethodDropdownOpen] =
    React.useState(false)
  const paymentMethodDropdownRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isPaymentMethodDropdownOpen) return undefined
    const handleClickOutside = (e: MouseEvent) => {
      if (
        paymentMethodDropdownRef.current &&
        !paymentMethodDropdownRef.current.contains(e.target as Node)
      ) {
        setIsPaymentMethodDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isPaymentMethodDropdownOpen])

  const handleSelectPaymentMethod = (pm: PaymentMethod) => {
    if (originalIndex !== -1 && onUpdateArrayItem) {
      onUpdateArrayItem('invoices', originalIndex, { paymentMethod: pm })
    }
    setIsPaymentMethodDropdownOpen(false)
  }

  return (
    <tr className="hover:tw-bg-base-50">
      <TableCheckbox
        disabled={false}
        checked={isChecked}
        onChange={() => toggleId(originalIndex)}
      />

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <DateEditor
          value={invoice.costDate}
          onCommit={(newDate) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('invoices', originalIndex, {
                costDate: newDate,
              })
            }
          }}
        />
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={invoice.invoiceNumber || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('invoices', originalIndex, {
                invoiceNumber: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={invoice.name || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('invoices', originalIndex, {
                name: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          inputMode="decimal"
          defaultValue={formatAmount(invoice.amount)}
          key={`amount-${originalIndex}-${invoice.amount}`}
          onBlur={(e) => {
            const val = e.target.value.replace(',', '.')
            if (originalIndex !== -1) {
              handleAmountChange(invoice, originalIndex, Number(val) || 0)
            }
          }}
          className="tw-w-24 tw-rounded tw-py-1 tw-text-sm focus:tw-outline-none"
        />
      </td>

      <td
        className="tw-min-w-[120px] tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={currencyDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsCurrencyDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {invoice.currency?.code || '-'}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isCurrencyDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-max-h-[320px] tw-min-w-[120px] tw-overflow-y-auto tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg">
              {currencies.map((c: any) => (
                <div
                  key={c.code || c.id}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                  onClick={() => handleSelectCurrency(c)}
                >
                  <span>{c.code}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          inputMode="decimal"
          defaultValue={formatRate(invoice.exchangeRate)}
          key={`rate-${originalIndex}-${invoice.exchangeRate}`}
          onBlur={(e) => {
            const val = e.target.value.replace(',', '.')
            if (originalIndex !== -1) {
              handleExchangeRateManualChange(
                invoice,
                originalIndex,
                Number(val) || 0,
              )
            }
          }}
          className="tw-w-24 tw-rounded tw-py-1 tw-text-sm focus:tw-outline-none"
        />
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <DateEditor
          value={invoice.exchangeRateDate}
          onCommit={(newDate) => {
            if (originalIndex !== -1 && newDate) {
              handleExchangeRateDateChange(invoice, originalIndex, newDate)
            }
          }}
        />
      </td>

      <td className="tw-px-3 tw-py-2.5" onClick={(e) => e.stopPropagation()}>
        <input
          type="text"
          value={invoice.table || ''}
          onChange={(e) => {
            if (originalIndex !== -1 && onUpdateArrayItem) {
              onUpdateArrayItem('invoices', originalIndex, {
                table: e.target.value,
              })
            }
          }}
          className="tw-w-full tw-rounded tw-py-1 tw-text-sm"
          placeholder="Wpisz..."
        />
      </td>

      <td
        className="tw-min-w-[150px] tw-px-3 tw-py-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        <div ref={paymentMethodDropdownRef} className="tw-relative">
          <div
            className="tw-flex tw-cursor-pointer tw-items-center tw-justify-between tw-gap-2"
            onClick={() => setIsPaymentMethodDropdownOpen((prev) => !prev)}
          >
            <p className="tw-text-sm tw-text-content-base">
              {invoice.paymentMethod != null
                ? getPaymentMethodName(invoice.paymentMethod, t)
                : '-'}
            </p>
            <FaIcon icon={faCaretDownSolid} />
          </div>
          {isPaymentMethodDropdownOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-1 tw-min-w-[180px] tw-rounded tw-border tw-border-base-300 tw-bg-white tw-py-1 tw-shadow-lg">
              {PAYMENT_METHOD_VALUES.map((pm) => (
                <div
                  key={pm}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content-base hover:tw-bg-base-100"
                  onClick={() => handleSelectPaymentMethod(pm)}
                >
                  <span>{getPaymentMethodName(pm, t)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>

      <TableTextRow>{formatAmount(invoice.plnAmount)}</TableTextRow>
    </tr>
  )
}

export default InvoiceRowItem
